import { useState } from "react";
import {
  resourceService,
  subscriptionService,
  taskService,
} from "@miplatform/api";
import {
  ASSIGNMENT_TEMPLATE_TYPES,
  HTTP_STATUSES,
} from "@miplatform/constants";
import { useComponentState, useResourceGroup } from "@miplatform/hooks";
import { createUUID } from "@miplatform/utils";

const getAssignmentTemplateUUID = createUUID();
const getCreateAssignmentTemplateUUID = createUUID();
const deleteTemplateUUID = createUUID();
const getRolesListUUID = createUUID();

export const useAssignmentTemplateViewModel = () => {
  const stateManager = useComponentState();
  const [assignmentTemplates, setAssignmentTemplates] = useState([]);
  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [assignmentTemplateOptions, setAssignmentTemplateOptions] = useState(
    []
  );
  const { resourceGroups, getResourceGroups } = useResourceGroup({
    service: resourceService,
  });
  const [roleOptions, setRoleOptions] = useState([]);

  const getAssignmentTemplates = () => {
    stateManager.abort(getAssignmentTemplateUUID);
    stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.getAll({
        sort: "name asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAssignmentTemplates(response.data);
        setAssignmentTemplateOptions(
          response.data
            .filter(
              (item) =>
                item.type !== ASSIGNMENT_TEMPLATE_TYPES.REVIEW &&
                item.type === ASSIGNMENT_TEMPLATE_TYPES.INSTALL
            )
            .map((item) => ({ label: item.name, value: item.id }))
        );
      }
    }, getAssignmentTemplateUUID);
  };

  const createAssignmentTemplate = (data) => {
    stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.create({
        data,
        toastMessageConfig: {
          success: {
            text: ` ${data.name} was created successfully.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.CREATED) {
        setOpenAddDrawer(false);
        getAssignmentTemplates();
      }
    }, getCreateAssignmentTemplateUUID);
  };

  const deleteAssignmentTemplate = (id, data) => {
    stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.delete({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: ` ${data.name} was deleted successfully.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        getAssignmentTemplates();
        setSelectedItem(null);
        setOpenDeleteModal(false);
      }
    }, deleteTemplateUUID);
  };

  const onCreate = (data) => {
    createAssignmentTemplate(data);
  };

  const onDelete = () => {
    deleteAssignmentTemplate(selectedItem.id, {
      modifiedOn: selectedItem.modifiedOn,
      name: selectedItem.name,
    });
  };

  const onDeleteCancel = () => {
    setOpenDeleteModal(false);
    setSelectedItem(null);
  };

  const getRoleOptions = async () => {
    stateManager.abort(getRolesListUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.roles.getAll({
        sort: "name asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRoleOptions(
          response.data.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
      }
      return null;
    }, getRolesListUUID);
  };

  return {
    stateManager,
    assignmentTemplateOptions,
    assignmentTemplates,
    getAssignmentTemplates,
    openAddDrawer,
    setOpenAddDrawer,
    createAssignmentTemplate,
    openDeleteModal,
    setOpenDeleteModal,
    deleteAssignmentTemplate,
    selectedItem,
    setSelectedItem,
    onCreate,
    onDelete,
    onDeleteCancel,
    getResourceGroups,
    getRoleOptions,
    roleOptions,
    resourceGroups: resourceGroups.map((item) => ({
      label: item.name,
      value: item.id,
    })),
  };
};
